import React from 'react';
import { bool, func, object, shape, string } from 'prop-types';

import clsx from 'clsx';

import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';

import Select from '../FormFields/Select';

import { TabContent } from '../TabPanel';

import defaultClasses from './RuleScoreFieldset.module.css';

const RuleScoreFieldset = ({
    disabled,
    errors,
    onBlur,
    onChange,
    onPatch,
    setFieldValue,
    touched,
    values
}) => {
    const handleEnabledCheckbox = (event, checkState) => {
        if (!checkState) {
            setFieldValue('fvoOutputDataType', '', false);
            setFieldValue('lowScoreValueLimit', '', false);
            setFieldValue('highScoreValueLimit', '', false);
            setFieldValue('lowScoreBooleanValueLimit', '', false);
            setFieldValue('customWeightEnabled', false, false);
            setFieldValue('customWeight', '', false);
        }

        onChange(event);
    };

    const handleOutputDataChange = event => {
        const { value } = event.target;

        if (value === 'analog') {
            setFieldValue('lowScoreBooleanValueLimit', '', false);
        } else if (value === 'boolean') {
            setFieldValue('lowScoreValueLimit', '', false);
            setFieldValue('highScoreValueLimit', '', false);
        }

        onChange(event);
    };

    const handleInputChange = event => {
        const { value } = event.target;

        if (!value || (value >= -100000 && value <= 100000)) {
            onChange(event);
        }
    };

    const handleWeightChange = event => {
        const { value } = event.target;

        if (!value || (value >= 0 && value <= 100)) {
            onChange(event);
        }
    };

    return (
        <TabContent>
            <div className={defaultClasses.fieldset}>
                <div className={defaultClasses.formContainer}>
                    <div className={defaultClasses.formColumn}>
                        <div className={defaultClasses.labelColumn}>
                            <div className={defaultClasses.topLabelGroup}>
                                <div className={defaultClasses.labelHeader}>
                                    <img src="/assets/images/scoringFlat.svg" alt="score icon" />
                                    <span>Score Settings</span>
                                </div>
                                <div className={defaultClasses.labelContent}>
                                    <p>Rule Score will be calculated based on a linear reset.  As the formula output moves between the 0 score value and the 100 score value, the rule score will be calculated proportionally.</p>
                                    <p>Formula output type should associate with the value type the formula will calculate.  Boolean for true/false and analog for numerical values.</p>
                                    <p>As the rule score approaches 0, it will proportionately decrease the target entity's (equipment or system) score based on its priority.  Or a custom weight can be chosen if desired to control exactly how much each rule decrements the parent entity's score.  For more details, see the help guide.</p>
                                </div>
                            </div>
                        </div>
                        <div className={defaultClasses.inputColumn}>
                            <FormControl className={defaultClasses.checkboxWrapper}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            disabled={!values.custom}
                                            checked={values.fvoEnabledScoring}
                                            value={values.fvoEnabledScoring}
                                            name="fvoEnabledScoring"
                                            color="primary"
                                            onChange={handleEnabledCheckbox}
                                        />
                                    }
                                    label="Use New Formula Based Scoring"
                                    className={defaultClasses.checkboxLabel}
                                />
                            </FormControl>
                            <React.Fragment>
                                {
                                    values.fvoEnabledScoring &&
                                    <FormControl className={defaultClasses.inputWrapper}>
                                        <Select
                                            errors={errors.fvoOutputDataType}
                                            disabled={disabled}
                                            label="Formula Output Data Type"
                                            onBlur={onBlur}
                                            onChange={handleOutputDataChange}
                                            name="fvoOutputDataType"
                                            required
                                            touched={touched.fvoOutputDataType}
                                            value={values.fvoOutputDataType ? values.fvoOutputDataType.toLowerCase() : values.fvoOutputDataType}
                                        >
                                            <MenuItem value="analog">Analog</MenuItem>
                                            <MenuItem value="boolean">Boolean</MenuItem>
                                        </Select>
                                    </FormControl>
                                }
                                {
                                    values.fvoEnabledScoring &&
                                    (values.fvoOutputDataType === 'Analog' || values.fvoOutputDataType === 'analog') &&
                                    <React.Fragment>
                                        <FormControl className={defaultClasses.inputWrapper}>
                                            <TextField
                                                id="lowScoreValueLimit"
                                                name="lowScoreValueLimit"
                                                type="number"
                                                value={values.lowScoreValueLimit}
                                                label="0 Score Value"
                                                onChange={handleInputChange}
                                                InputLabelProps={{
                                                    classes: {
                                                        error:   defaultClasses.error,
                                                        focused: defaultClasses.labelFocused,
                                                        root:    defaultClasses.label
                                                    }
                                                }}
                                                InputProps={{
                                                    onBlur:  onBlur,
                                                    classes: {
                                                        error:     defaultClasses.cssErrorUnderline,
                                                        underline: defaultClasses.cssUnderline,
                                                        root:      defaultClasses.input
                                                    }
                                                }}
                                                FormHelperTextProps={{
                                                    classes: {
                                                        error: defaultClasses.error
                                                    }
                                                }}
                                                error={Boolean(errors.lowScoreValueLimit) && Boolean(touched.lowScoreValueLimit)}
                                                helperText={Boolean(touched.lowScoreValueLimit) && errors.lowScoreValueLimit}
                                            />
                                        </FormControl>
                                        <FormControl className={defaultClasses.inputWrapper}>
                                            <TextField
                                                id="highScoreValueLimit"
                                                name="highScoreValueLimit"
                                                type="number"
                                                value={values.highScoreValueLimit}
                                                label="100 Score Value"
                                                onChange={handleInputChange}
                                                InputLabelProps={{
                                                    classes: {
                                                        error:   defaultClasses.error,
                                                        focused: defaultClasses.labelFocused,
                                                        root:    defaultClasses.label
                                                    }
                                                }}
                                                InputProps={{
                                                    onBlur:  onBlur,
                                                    classes: {
                                                        error:     defaultClasses.cssErrorUnderline,
                                                        underline: defaultClasses.cssUnderline,
                                                        root:      defaultClasses.input
                                                    }
                                                }}
                                                FormHelperTextProps={{
                                                    classes: {
                                                        error: defaultClasses.error
                                                    }
                                                }}
                                                error={Boolean(errors.highScoreValueLimit) && Boolean(touched.highScoreValueLimit)}
                                                helperText={Boolean(touched.highScoreValueLimit) && errors.highScoreValueLimit}
                                            />
                                        </FormControl>
                                    </React.Fragment>
                                }
                                {
                                    values.fvoEnabledScoring &&
                                    (values.fvoOutputDataType === 'boolean' || values.fvoOutputDataType === 'Boolean') &&
                                    <FormControl className={defaultClasses.inputWrapper}>
                                        <Select
                                            errors={errors.lowScoreBooleanValueLimit}
                                            label="0 Score Value"
                                            onBlur={onBlur}
                                            onChange={onChange}
                                            name="lowScoreBooleanValueLimit"
                                            required
                                            touched={touched.lowScoreBooleanValueLimit}
                                            value={values.lowScoreBooleanValueLimit}
                                        >
                                            <MenuItem value={true}>True</MenuItem>
                                            <MenuItem value={false}>False</MenuItem>
                                        </Select>
                                    </FormControl>
                                }
                                {
                                    values.fvoEnabledScoring &&
                                    <React.Fragment>
                                        <FormControl className={clsx(defaultClasses.checkboxWrapper, defaultClasses.inputWrapper)}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={values.customWeightEnabled}
                                                        value={values.customWeightEnabled}
                                                        name="customWeightEnabled"
                                                        color="primary"
                                                        onChange={onChange}
                                                    />
                                                }
                                                label="Use Custom Weighting (0-100)"
                                                className={defaultClasses.checkboxLabel}
                                            />
                                        </FormControl>
                                    </React.Fragment>
                                }
                                {
                                    values.fvoEnabledScoring &&
                                    values.customWeightEnabled &&
                                    <React.Fragment>
                                        <FormControl className={defaultClasses.inputWrapper}>
                                            <TextField
                                                id="customWeight"
                                                name="customWeight"
                                                type="number"
                                                value={values.customWeight}
                                                label="Custom Weight"
                                                onChange={handleWeightChange}
                                                InputLabelProps={{
                                                    classes: {
                                                        error:   defaultClasses.error,
                                                        focused: defaultClasses.labelFocused,
                                                        root:    defaultClasses.label
                                                    }
                                                }}
                                                InputProps={{
                                                    onBlur:  onBlur,
                                                    classes: {
                                                        error:     defaultClasses.cssErrorUnderline,
                                                        underline: defaultClasses.cssUnderline,
                                                        root:      defaultClasses.input
                                                    }
                                                }}
                                                FormHelperTextProps={{
                                                    classes: {
                                                        error: defaultClasses.error
                                                    }
                                                }}
                                                error={Boolean(errors.customWeight) && Boolean(touched.customWeight)}
                                                helperText={Boolean(touched.customWeight) && errors.customWeight}
                                            />
                                        </FormControl>
                                    </React.Fragment>
                                }
                            </React.Fragment>
                        </div>
                    </div>
                </div>
            </div>
        </TabContent>
    );
};

RuleScoreFieldset.defaultProps = {
    disabled: false,
    errors:   {},
    onChange: () => {},
    onPatch:  () => {},
    touched:  {},
    values:   {}
};

RuleScoreFieldset.propTypes = {
    disabled: bool,
    errors:   shape({
        priority: string
    }),
    touched: shape({
        priority: bool
    }),
    onChange: func,
    onPatch:  func,
    values:   object
};

export default RuleScoreFieldset;
